import type { EventType } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useCallback, useState } from 'react'
import { View } from 'react-native'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox'
import { Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Calendar } from '~/components/ui/calendar'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'

/* eslint-disable i18next/no-literal-string */

export type EventFormData = {
  title: string
  description: string
  eventType: EventType
  eventDate: Date
}

type EventFormProps = {
  initialData?: Partial<EventFormData>
  onSubmit: (data: EventFormData) => void
  onCancel: () => void
  isSubmitting?: boolean
  submitLabel?: string
}

export function EventForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel,
}: EventFormProps) {
  const { t } = useTranslations('events')

  const eventTypeOptions = [
    { label: t(($) => $.eventTypes.showing), value: 'showing' as const },
    { label: t(($) => $.eventTypes.appraisal), value: 'appraisal' as const },
  ]
  const [title, setTitle] = useState(initialData?.title ?? '')
  const [description, setDescription] = useState(initialData?.description ?? '')
  const [eventType, setEventType] = useState<EventType>(initialData?.eventType ?? 'showing')
  const [eventDate, setEventDate] = useState<Date>(initialData?.eventDate ?? new Date())

  const handleSubmit = useCallback(() => {
    if (!title.trim()) {
      return
    }

    onSubmit({
      title: title.trim(),
      description: description.trim(),
      eventType,
      eventDate,
    })
  }, [title, description, eventType, eventDate, onSubmit])

  const isValid = title.trim().length > 0

  return (
    <ContentBox
      contentContainerClassName="justify-start gap-3 p-0"
      header={
        <BoxActionsHeader>
          <Heading2 className="text-center">
            {initialData ? t(($) => $.form.editTitle) : t(($) => $.form.newTitle)}
          </Heading2>
        </BoxActionsHeader>
      }
      footer={
        <View className="flex-1 flex-row justify-between gap-3 px-4 pb-6">
          <Button variant="ghost" size="lg" onPress={onCancel} disabled={isSubmitting}>
            <ButtonLabel>{t(($) => $.form.cancel)}</ButtonLabel>
          </Button>
          <Button
            variant="primary"
            size="lg"
            onPress={handleSubmit}
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}>
            <ButtonLabel>{submitLabel ?? t(($) => $.form.save)}</ButtonLabel>
          </Button>
        </View>
      }>
      <View className="gap-8 px-4 py-6">
        <View className="gap-2">
          <Label>{t(($) => $.form.title)}</Label>
          <Input
            value={title}
            onChangeText={setTitle}
            placeholder={t(($) => $.form.titlePlaceholder)}
            variant="box"
            className="placeholder:text-tertiary-foreground"
          />
        </View>

        <View className="gap-2">
          <Label>{t(($) => $.form.description)}</Label>
          <Input
            value={description}
            onChangeText={setDescription}
            placeholder={t(($) => $.form.descriptionPlaceholder)}
            multiline
            numberOfLines={3}
            variant="box"
            className="min-h-[80px] placeholder:text-tertiary-foreground"
          />
        </View>

        <View className="gap-4">
          <Label>{t(($) => $.form.type)}</Label>
          <Select
            value={{
              value: eventType,
              label: eventTypeOptions.find((option) => option.value === eventType)?.label ?? '',
            }}
            onValueChange={(option) => {
              if (option?.value) {
                setEventType(option.value as EventType)
              }
            }}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t(($) => $.form.typeSelectPlaceholder)} />
            </SelectTrigger>
            <SelectContent>
              {eventTypeOptions.map((option) => (
                <SelectItem key={option.value} label={option.label} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </View>

        <View className="gap-4">
          <Label>{t(($) => $.form.dateTime)}</Label>
          <Calendar
            date={eventDate}
            onChange={setEventDate}
            timePicker
            minDate={new Date()}
          />
        </View>
      </View>
    </ContentBox>
  )
}
